import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faServer, faTrash, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
import LdapGroupPickerDialog from 'Components/LdapGroupPickerDialog';
import { EUserGroup, GetUserData, PermissionDefinitionDto, PermissionGroupSummaryDto } from 'apiClient/Api';

// Gruppen-Detail + Mitglieder sind serverseitig untypisiert (any) – hier defensiv nachgebildet.
interface GroupMember {
    userId?: string;
    name?: string | null;
    email?: string | null;
    /** 'Manual' = von Hand zugewiesen, 'Ldap' = beim Login aus der AD-Gruppe gesetzt. */
    source?: string | null;
}
interface GroupDetail {
    id?: string;
    name?: string;
    description?: string | null;
    permissions?: string[];
    members?: GroupMember[];
    ldapGroupCn?: string | null;
    ldapRole?: EUserGroup | null;
}

/** Rollen, die eine AD-Gruppe vergeben kann — CUSTOMER ist bewusst nicht dabei. */
const ASSIGNABLE_ROLES: { value: EUserGroup; label: string }[] = [
    { value: EUserGroup.SUPER_ADMIN, label: 'SUPER_ADMIN' },
    { value: EUserGroup.ADMIN, label: 'ADMIN' },
    { value: EUserGroup.DEVELOPMENT, label: 'DEVELOPMENT' },
    { value: EUserGroup.OFFICE, label: 'OFFICE' },
];

const PermissionChecklist: React.FC<{
    catalog: PermissionDefinitionDto[];
    granted: Set<string>;
    onToggle: (key: string, on: boolean) => void;
}> = ({ catalog, granted, onToggle }) => {
    const byCategory = useMemo(() => {
        const map = new Map<string, PermissionDefinitionDto[]>();
        for (const p of catalog) {
            const cat = p.category || 'Allgemein';
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat)!.push(p);
        }
        return Array.from(map.entries());
    }, [catalog]);

    return (
        <Box>
            {byCategory.map(([cat, perms]) => (
                <Box key={cat} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {cat}
                    </Typography>
                    {perms.map((p) => (
                        <FormControlLabel
                            key={p.key}
                            sx={{ display: 'flex', alignItems: 'flex-start', ml: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={granted.has(p.key || '')}
                                    onChange={(e) => onToggle(p.key || '', e.target.checked)}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {p.key}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {p.description}
                                    </Typography>
                                </Box>
                            }
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );
};

const GroupDetailPanel: React.FC<{
    groupId: string;
    catalog: PermissionDefinitionDto[];
    users: GetUserData[];
    onChanged: () => void;
}> = ({ groupId, catalog, users, onChanged }) => {
    const { api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const [detail, setDetail] = useState<GroupDetail | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [granted, setGranted] = useState<Set<string>>(new Set());
    const [pickUser, setPickUser] = useState<GetUserData | null>(null);
    const [loading, setLoading] = useState(true);
    const [ldapGroupCn, setLdapGroupCn] = useState('');
    const [ldapRole, setLdapRole] = useState<EUserGroup | ''>('');
    const [pickerOpen, setPickerOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.permissions
            .permissionAdminGetGroup(groupId)
            .then((r) => {
                const d = r.data as unknown as GroupDetail;
                setDetail(d);
                setName(d.name || '');
                setDescription(d.description || '');
                setGranted(new Set(d.permissions || []));
                setLdapGroupCn(d.ldapGroupCn || '');
                setLdapRole(d.ldapRole ?? '');
            })
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, groupId, apiError]);

    useEffect(() => load(), [load]);

    const saveStammdaten = async () => {
        try {
            await api.permissions.permissionAdminUpdateGroup(groupId, {
                name,
                description: description || null,
                ldapGroupCn: ldapGroupCn.trim() || null,
                ldapRole: ldapRole === '' ? null : ldapRole,
            });
            enqueueSnackbar('Gruppe gespeichert.', { variant: 'success' });
            load();
            onChanged();
        } catch (e) {
            apiError(e);
        }
    };

    const saveGrants = async () => {
        try {
            await api.permissions.permissionAdminSetGrants(groupId, { permissions: Array.from(granted) });
            enqueueSnackbar('Berechtigungen gespeichert.', { variant: 'success' });
            onChanged();
        } catch (e) {
            apiError(e);
        }
    };

    const addMember = async () => {
        if (!pickUser?.id) return;
        try {
            await api.permissions.permissionAdminAddMember(groupId, { userId: pickUser.id });
            setPickUser(null);
            load();
            onChanged();
        } catch (e) {
            apiError(e);
        }
    };

    const removeMember = async (userId: string) => {
        try {
            await api.permissions.permissionAdminRemoveMember(groupId, userId);
            load();
            onChanged();
        } catch (e) {
            apiError(e);
        }
    };

    const deleteGroup = async () => {
        if (!window.confirm(`Gruppe „${name}" löschen?`)) return;
        try {
            await api.permissions.permissionAdminDeleteGroup(groupId);
            enqueueSnackbar('Gruppe gelöscht.', { variant: 'success' });
            onChanged();
        } catch (e) {
            apiError(e);
        }
    };

    if (loading || !detail) return <LinearProgress />;

    const members = detail.members || [];
    const memberIds = new Set(members.map((m) => m.userId));
    const userLabel = (u: GetUserData) => `${u.name || ''} ${u.eMail ? `<${u.eMail}>` : ''}`.trim();

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Stammdaten
                </Typography>
                <Stack spacing={1.5}>
                    <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                    <TextField
                        size="small"
                        label="Beschreibung"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                    />

                    <Divider textAlign="left">
                        <Typography variant="caption" color="text.secondary">
                            AD-Verknüpfung (optional)
                        </Typography>
                    </Divider>
                    <Typography variant="body2" color="text.secondary">
                        Ist eine AD-Gruppe verknüpft, werden die Mitglieder dieser Gruppe bei jedem Login automatisch
                        gepflegt. Die Rolle ist optional — ohne Rolle vergibt die Gruppe nur die Berechtigungen von
                        oben. <strong>Mindestens eine Gruppe muss eine Rolle vergeben</strong>, sonst wird jeder
                        AD-Login abgelehnt.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                            size="small"
                            fullWidth
                            label="AD-Gruppe (CN)"
                            placeholder="Support-Office"
                            value={ldapGroupCn}
                            onChange={(e) => setLdapGroupCn(e.target.value)}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<FontAwesomeIcon icon={faServer} />}
                            onClick={() => setPickerOpen(true)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Aus AD wählen
                        </Button>
                    </Stack>
                    <TextField
                        size="small"
                        select
                        label="Rolle, die diese Gruppe vergibt"
                        value={ldapRole === '' ? '' : String(ldapRole)}
                        onChange={(e) => setLdapRole(e.target.value === '' ? '' : (Number(e.target.value) as EUserGroup))}
                        disabled={!ldapGroupCn.trim()}
                        helperText={!ldapGroupCn.trim() ? 'Erst eine AD-Gruppe verknüpfen' : undefined}
                    >
                        <MenuItem value="">
                            <em>keine Rolle — nur Berechtigungen</em>
                        </MenuItem>
                        {ASSIGNABLE_ROLES.map((r) => (
                            <MenuItem key={r.value} value={String(r.value)}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Box>
                        <Button variant="contained" onClick={saveStammdaten} disabled={!name.trim()}>
                            Speichern
                        </Button>
                    </Box>
                </Stack>
            </Box>

            <LdapGroupPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={(cn) => setLdapGroupCn(cn)}
            />

            <Divider />

            <Box>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Berechtigungen
                    </Typography>
                    <Button variant="contained" onClick={saveGrants}>
                        Berechtigungen speichern
                    </Button>
                </Stack>
                <PermissionChecklist
                    catalog={catalog}
                    granted={granted}
                    onToggle={(key, on) =>
                        setGranted((prev) => {
                            const next = new Set(prev);
                            if (on) next.add(key);
                            else next.delete(key);
                            return next;
                        })
                    }
                />
            </Box>

            <Divider />

            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Mitglieder ({members.length})
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 320 }}
                        options={users.filter((u) => !memberIds.has(u.id))}
                        value={pickUser}
                        getOptionLabel={userLabel}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        onChange={(_, v) => setPickUser(v)}
                        renderInput={(params) => <TextField {...params} label="Benutzer hinzufügen" />}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                        onClick={addMember}
                        disabled={!pickUser}
                    >
                        Hinzufügen
                    </Button>
                </Stack>
                <List dense>
                    {members.map((m) => {
                        const fromAd = m.source === 'Ldap';
                        return (
                            <ListItem
                                key={m.userId}
                                secondaryAction={
                                    <Tooltip
                                        title={
                                            fromAd
                                                ? 'Kommt aus der AD-Gruppe und wird beim nächsten Login wieder gesetzt — im Active Directory entfernen.'
                                                : 'Mitglied entfernen'
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                disabled={fromAd}
                                                onClick={() => m.userId && removeMember(m.userId)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                }
                            >
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                            <span>{m.name || m.userId}</span>
                                            {fromAd && <Chip size="small" color="primary" variant="outlined" label="AD" />}
                                        </Stack>
                                    }
                                    secondary={m.email}
                                />
                            </ListItem>
                        );
                    })}
                    {members.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Noch keine Mitglieder.
                        </Typography>
                    )}
                </List>
            </Box>

            <Divider />
            <Box>
                <Button color="error" startIcon={<FontAwesomeIcon icon={faTrash} />} onClick={deleteGroup}>
                    Gruppe löschen
                </Button>
            </Box>
        </Stack>
    );
};

export const PermissionGroups: React.FC = () => {
    const { api, apiError, setTitle } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const [groups, setGroups] = useState<PermissionGroupSummaryDto[]>([]);
    const [catalog, setCatalog] = useState<PermissionDefinitionDto[]>([]);
    const [users, setUsers] = useState<GetUserData[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');

    useEffect(() => setTitle('Berechtigungen'), [setTitle]);

    const reload = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.permissions.permissionAdminListGroups(),
            api.permissions.permissionAdminGetCatalog(),
            api.user.userListUsers(),
        ])
            .then(([g, c, u]) => {
                setGroups(g.data ?? []);
                setCatalog(c.data ?? []);
                setUsers(u.data ?? []);
            })
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    const create = async () => {
        if (!newName.trim()) return;
        try {
            await api.permissions.permissionAdminCreateGroup({ name: newName.trim(), description: newDesc || null });
            enqueueSnackbar('Gruppe angelegt.', { variant: 'success' });
            setCreateOpen(false);
            setNewName('');
            setNewDesc('');
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    return (
        <Grid size={12}>
            <Title>Berechtigungen</Title>
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 1 }}>
                        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Gruppen
                            </Typography>
                            <Button size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setCreateOpen(true)}>
                                Neu
                            </Button>
                        </Stack>
                        <List dense>
                            {groups.map((g) => (
                                <ListItemButton
                                    key={g.id}
                                    selected={selected === g.id}
                                    onClick={() => setSelected(g.id ?? null)}
                                >
                                    <ListItemText
                                        primary={g.name}
                                        secondary={
                                            <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                <Chip size="small" label={`${g.memberCount ?? 0} Mitgl.`} />
                                                <Chip size="small" variant="outlined" label={`${g.grantCount ?? 0} Rechte`} />
                                                {g.ldapGroupCn && (
                                                    <Chip
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        label={
                                                            g.ldapRole !== null && g.ldapRole !== undefined
                                                                ? `AD: ${g.ldapGroupCn} ⇒ ${EUserGroup[g.ldapRole]}`
                                                                : `AD: ${g.ldapGroupCn}`
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            ))}
                            {groups.length === 0 && !loading && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                    Noch keine Gruppen.
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 2 }}>
                        {selected ? (
                            <GroupDetailPanel
                                key={selected}
                                groupId={selected}
                                catalog={catalog}
                                users={users}
                                onChanged={reload}
                            />
                        ) : (
                            <Typography color="text.secondary">Wähle links eine Gruppe oder lege eine neue an.</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Neue Gruppe</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField size="small" label="Name" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} />
                        <TextField size="small" label="Beschreibung" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} multiline minRows={2} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
                    <Button variant="contained" onClick={create} disabled={!newName.trim()}>
                        Anlegen
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default PermissionGroups;
